import { useCallback, useEffect, useState } from 'react';

// Profile image URLs that already failed to preload. Kept at module scope so the
// guard survives TopBar remounts (it remounts on every route change).
const failedProfileImageUrls = new Set();

/**
 * Profile photo URL to display. The URL is preloaded and cached once it loads;
 * a URL that fails triggers at most one profile refetch (a stale download URL
 * is the usual cause), and a broken cached URL is dropped.
 */
const useProfileImage = (profileImageUrl, refetchProfileData) => {
  const [cachedProfileUrl, setCachedProfileUrl] = useState('');

  useEffect(() => {
    if (!profileImageUrl || profileImageUrl === cachedProfileUrl) return;
    const img = new Image();
    img.onload = () => setCachedProfileUrl(profileImageUrl);
    img.onerror = () => {
      console.log('Failed to pre-load profile image');
      // Retry the fetch at most once per URL; a component ref would be wiped
      // by the remount the refetch causes, and the retry would loop.
      if (!cachedProfileUrl && !failedProfileImageUrls.has(profileImageUrl)) {
        failedProfileImageUrls.add(profileImageUrl);
        refetchProfileData();
      }
    };
    img.src = profileImageUrl;
  }, [profileImageUrl, cachedProfileUrl, refetchProfileData]);

  const displayUrl = cachedProfileUrl || profileImageUrl || '';

  const handleImageError = useCallback(() => {
    if (cachedProfileUrl && cachedProfileUrl === displayUrl) {
      setCachedProfileUrl('');
      refetchProfileData();
    }
  }, [cachedProfileUrl, displayUrl, refetchProfileData]);

  return { displayUrl, handleImageError };
};

export default useProfileImage;
